export const physicsICourse = {
  id: 'physics-i',
  title: 'Physics I: Mechanics and Applications',
  description: 'Comprehensive introduction to classical mechanics, motion, forces, energy, and their applications in computer science, engineering, and technology.',
  credits: 2,
  duration: '15 weeks',
  level: 'Beginner',
  category: 'Coding',
  learningObjectives: [
    'Understand fundamental principles of classical mechanics',
    'Apply mathematical models to analyze motion and forces',
    'Solve physics problems using computational methods',
    'Connect physics concepts to computer science applications',
    'Develop analytical and problem-solving skills',
    'Use simulation and modeling tools for physics analysis'
  ],
  prerequisites: ['Calculus I', 'Basic programming knowledge'],
  skills: [
    'Classical Mechanics',
    'Mathematical Modeling',
    'Problem Solving',
    'Data Analysis',
    'Computational Physics',
    'Scientific Programming',
    'Simulation Techniques',
    'Physics Applications',
    'Mathematical Analysis',
    'Laboratory Techniques',
    'Measurement and Uncertainty',
    'Scientific Communication'
  ],
  marketDemand: 'High',
  averageSalary: '$65,000 - $120,000',
  careerPaths: [
    'Software Developer',
    'Game Physics Programmer',
    'Simulation Engineer',
    'Data Scientist',
    'Research Scientist',
    'Robotics Engineer',
    'Computer Graphics Developer',
    'Scientific Software Developer',
    'Systems Analyst',
    'Engineering Consultant',
    'Technical Analyst',
    'Educational Technology Developer'
  ],
  industryApplications: [
    'Game Development and Physics Engines',
    'Computer Graphics and Animation',
    'Robotics and Automation',
    'Scientific Computing',
    'Engineering Simulation',
    'Virtual Reality Systems',
    'Automotive Software',
    'Aerospace Engineering',
    'Medical Device Development',
    'Financial Modeling',
    'Machine Learning Applications',
    'Educational Software'
  ],
  modules: [
    {
      id: 'motion-kinematics',
      title: 'Motion and Kinematics',
      duration: '4 weeks',
      learningObjectives: [
        'Understand position, velocity, and acceleration relationships',
        'Analyze one-dimensional and two-dimensional motion',
        'Apply kinematic equations to solve motion problems',
        'Model projectile motion and circular motion'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Fundamentals of Motion',
          content: `# Motion and Kinematics

## Basic Concepts

### Position and Displacement
- **Position**: Location of an object in space
- **Displacement**: Change in position (vector quantity)
- **Distance**: Total path length (scalar quantity)

### Velocity and Speed
- **Average Velocity**: v̄ = Δx/Δt = (x₂ - x₁)/(t₂ - t₁)
- **Instantaneous Velocity**: v = dx/dt (derivative of position)
- **Speed**: Magnitude of velocity (always positive)

### Acceleration
- **Average Acceleration**: ā = Δv/Δt = (v₂ - v₁)/(t₂ - t₁)
- **Instantaneous Acceleration**: a = dv/dt = d²x/dt²

## Kinematic Equations (Constant Acceleration)

### One-Dimensional Motion
1. v = v₀ + at
2. x = x₀ + v₀t + ½at²
3. v² = v₀² + 2a(x - x₀)
4. x = x₀ + ½(v₀ + v)t

Where:
- x₀ = initial position
- v₀ = initial velocity
- v = final velocity
- a = acceleration
- t = time

### Two-Dimensional Motion
**Vector Components:**
- x-component: vₓ = v cos θ
- y-component: vᵧ = v sin θ
- Magnitude: |v| = √(vₓ² + vᵧ²)
- Direction: θ = tan⁻¹(vᵧ/vₓ)

## Projectile Motion

### Key Principles
- Horizontal and vertical motions are independent
- Horizontal velocity remains constant (no air resistance)
- Vertical motion follows free fall equations

### Projectile Motion Equations
- **Horizontal**: x = v₀ₓt = v₀ cos θ₀ · t
- **Vertical**: y = v₀ᵧt - ½gt² = v₀ sin θ₀ · t - ½gt²
- **Range**: R = (v₀² sin 2θ₀)/g
- **Maximum Height**: H = (v₀² sin² θ₀)/(2g)

## Computational Implementation

\`\`\`javascript
class KinematicsSimulator {
  constructor() {
    this.g = 9.81; // gravitational acceleration (m/s²)
  }
  
  // Calculate position using kinematic equation
  calculatePosition(x0, v0, a, t) {
    return x0 + v0 * t + 0.5 * a * t * t;
  }
  
  // Calculate velocity using kinematic equation
  calculateVelocity(v0, a, t) {
    return v0 + a * t;
  }
  
  // Simulate projectile motion
  simulateProjectile(v0, angle, dt = 0.01) {
    const angleRad = angle * Math.PI / 180;
    const v0x = v0 * Math.cos(angleRad);
    const v0y = v0 * Math.sin(angleRad);
    
    const trajectory = [];
    let t = 0;
    let y = 0;
    
    while (y >= 0) {
      const x = v0x * t;
      y = v0y * t - 0.5 * this.g * t * t;
      
      if (y >= 0) {
        trajectory.push({ t, x, y });
      }
      t += dt;
    }
    
    return {
      trajectory,
      range: trajectory[trajectory.length - 1].x,
      maxHeight: Math.max(...trajectory.map(p => p.y)),
      flightTime: trajectory[trajectory.length - 1].t
    };
  }
  
  // Calculate optimal launch angle for maximum range
  findOptimalAngle(v0) {
    let maxRange = 0;
    let optimalAngle = 0;
    
    for (let angle = 1; angle <= 89; angle++) {
      const result = this.simulateProjectile(v0, angle);
      if (result.range > maxRange) {
        maxRange = result.range;
        optimalAngle = angle;
      }
    }
    
    return { angle: optimalAngle, range: maxRange };
  }
  
  // Analyze motion data
  analyzeMotion(positions, times) {
    const velocities = [];
    const accelerations = [];
    
    // Calculate velocities (numerical differentiation)
    for (let i = 1; i < positions.length; i++) {
      const dt = times[i] - times[i-1];
      const dx = positions[i] - positions[i-1];
      velocities.push(dx / dt);
    }
    
    // Calculate accelerations
    for (let i = 1; i < velocities.length; i++) {
      const dt = times[i+1] - times[i];
      const dv = velocities[i] - velocities[i-1];
      accelerations.push(dv / dt);
    }
    
    return {
      velocities,
      accelerations,
      avgVelocity: velocities.reduce((a, b) => a + b, 0) / velocities.length,
      avgAcceleration: accelerations.reduce((a, b) => a + b, 0) / accelerations.length
    };
  }
}
\`\`\`

## Applications in Computer Science

### Game Physics
- Character movement and jumping mechanics
- Projectile trajectory for bullets, balls, particles
- Collision prediction and detection
- Realistic animation timing

### Computer Graphics
- Animation curves and motion paths
- Camera movement and tracking
- Particle system dynamics
- Realistic object behavior

### Robotics
- Path planning and navigation
- Motor control and positioning
- Sensor data interpretation
- Movement optimization

### Simulation Software
- Scientific modeling
- Engineering analysis
- Educational demonstrations
- Virtual experiments`
        },
        {
          type: 'interactive_exercise',
          title: 'Motion Simulator and Analyzer',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Create a comprehensive motion analysis tool that:

1. Simulates various types of motion (linear, projectile, circular)
2. Analyzes real motion data to extract physics parameters
3. Visualizes motion paths and graphs
4. Compares theoretical predictions with experimental data
5. Optimizes motion parameters for specific goals

Your simulator should handle:
- One-dimensional motion with constant acceleration
- Two-dimensional projectile motion
- Circular motion analysis
- Data fitting and parameter extraction
- Motion optimization problems

Build a tool that demonstrates physics principles through interactive simulation.`,
            initialCode: `class MotionAnalyzer {
  constructor() {
    this.gravity = 9.81; // m/s²
  }
  
  // Simulate 1D motion with constant acceleration
  simulate1DMotion(x0, v0, a, duration, dt = 0.01) {
    // Generate position, velocity, acceleration data over time
    // Return: { time: array, position: array, velocity: array }
  }
  
  // Simulate projectile motion
  simulateProjectile(v0, angle, height = 0, dt = 0.01) {
    // Calculate 2D trajectory with gravity
    // Return: { trajectory: array, range: number, maxHeight: number, flightTime: number }
  }
  
  // Analyze experimental motion data
  analyzeExperimentalData(timeData, positionData) {
    // Extract velocity and acceleration from position data
    // Fit kinematic equations to determine motion parameters
    // Return: { v0: number, acceleration: number, r_squared: number }
  }
  
  // Find optimal launch parameters
  optimizeLaunch(targetRange, constraints = {}) {
    // Find velocity and angle to hit target with constraints
    // constraints: { maxVelocity, maxAngle, minAngle }
    // Return: { velocity: number, angle: number, trajectory: array }
  }
  
  // Compare theoretical vs experimental results
  compareResults(theoretical, experimental) {
    // Calculate differences and statistical measures
    // Return: { rmse: number, correlation: number, maxError: number }
  }
  
  // Generate motion visualization data
  generateVisualization(motionType, parameters) {
    // Create data suitable for plotting
    // motionType: 'linear', 'projectile', 'circular'
    // Return: formatted data for charts/graphs
  }
}

// Physics utility functions
const PhysicsUtils = {
  // Numerical differentiation
  differentiate: (yData, xData) => {
    const derivatives = [];
    for (let i = 1; i < yData.length; i++) {
      const dy = yData[i] - yData[i-1];
      const dx = xData[i] - xData[i-1];
      derivatives.push(dy / dx);
    }
    return derivatives;
  },
  
  // Linear regression for motion analysis
  linearRegression: (xData, yData) => {
    const n = xData.length;
    const sumX = xData.reduce((a, b) => a + b, 0);
    const sumY = yData.reduce((a, b) => a + b, 0);
    const sumXY = xData.reduce((sum, x, i) => sum + x * yData[i], 0);
    const sumXX = xData.reduce((sum, x) => sum + x * x, 0);
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    return { slope, intercept };
  }
};

// Test implementation
const analyzer = new MotionAnalyzer();

// Test projectile motion: ball thrown at 30° with 20 m/s
const projectile = analyzer.simulateProjectile(20, 30);
console.log("Projectile range:", projectile.range.toFixed(2) + "m");
console.log("Max height:", projectile.maxHeight.toFixed(2) + "m");
console.log("Flight time:", projectile.flightTime.toFixed(2) + "s");

// Test optimization: hit target 15m away
const optimal = analyzer.optimizeLaunch(15, { maxVelocity: 25 });
console.log("Optimal angle:", optimal.angle.toFixed(1) + "°");
console.log("Required velocity:", optimal.velocity.toFixed(1) + "m/s");`,
            expectedOutput: `Projectile range: 35.32m
Max height: 5.10m
Flight time: 2.04s
Optimal angle: 26.7°
Required velocity: 12.8m/s`,
            hints: [
              'Use kinematic equations: x = x0 + v0*t + 0.5*a*t²',
              'For projectile motion: vx = v0*cos(θ), vy = v0*sin(θ) - g*t',
              'Calculate range using: R = v0²*sin(2θ)/g',
              'Use numerical differentiation to find velocity from position data',
              'Optimize using calculus or iterative search methods',
              'Theoretical range for 20 m/s at 30°: ≈ 35.3m'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Motion Analysis Lab',
          title: 'Kinematic Data Analysis and Simulation',
          points: 75,
          dueWeek: 4
        }
      ]
    },
    {
      id: 'forces-dynamics',
      title: 'Forces and Dynamics',
      duration: '5 weeks',
      learningObjectives: [
        'Understand Newton\'s laws of motion and their applications',
        'Analyze force systems and their effects on motion',
        'Apply friction, tension, and normal force concepts',
        'Solve dynamics problems using free body diagrams'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Newton\'s Laws and Force Analysis',
          content: `# Forces and Dynamics

## Newton's Laws of Motion

### First Law (Law of Inertia)
"An object at rest stays at rest, and an object in motion stays in motion at constant velocity, unless acted upon by a net external force."

**Mathematical Statement**: ΣF = 0 → a = 0

### Second Law (F = ma)
"The acceleration of an object is directly proportional to the net force acting on it and inversely proportional to its mass."

**Mathematical Statement**: ΣF = ma

### Third Law (Action-Reaction)
"For every action, there is an equal and opposite reaction."

**Mathematical Statement**: F₁₂ = -F₂₁

## Types of Forces

### Gravitational Force
- **Weight**: W = mg (near Earth's surface)
- **Universal Gravitation**: F = Gm₁m₂/r²

### Normal Force
- Force perpendicular to contact surface
- Prevents objects from passing through surfaces
- Can vary based on other forces present

### Friction Forces
**Static Friction**: fₛ ≤ μₛN
- Prevents motion when object is stationary
- Maximum value: fₛ,max = μₛN

**Kinetic Friction**: fₖ = μₖN
- Opposes motion when object is sliding
- Generally μₖ < μₛ

### Tension Force
- Force transmitted through strings, ropes, cables
- Tension same throughout massless string
- Direction along the string

### Applied Forces
- External forces applied to objects
- Can be constant or variable
- Include pushes, pulls, magnetic forces

## Free Body Diagrams

### Steps for Creating FBDs
1. **Isolate the object** of interest
2. **Identify all forces** acting on the object
3. **Draw force vectors** from center of mass
4. **Choose coordinate system** (usually align with motion)
5. **Label all forces** with appropriate symbols

### Problem-Solving Strategy
1. Draw free body diagram
2. Choose coordinate system
3. Apply Newton's second law: ΣF = ma
4. Write component equations:
   - ΣFₓ = maₓ
   - ΣFᵧ = maᵧ
5. Solve system of equations

## Applications and Examples

### Inclined Plane Problems
For object on incline at angle θ:
- **Parallel to incline**: mg sin θ - f = ma
- **Perpendicular to incline**: N - mg cos θ = 0

### Atwood Machine
Two masses connected by rope over pulley:
- m₁g - T = m₁a
- T - m₂g = m₂a
- Solution: a = (m₁ - m₂)g/(m₁ + m₂)

## Computational Implementation

\`\`\`javascript
class ForceAnalyzer {
  constructor() {
    this.g = 9.81; // gravitational acceleration
  }
  
  // Calculate net force from force components
  calculateNetForce(forces) {
    // forces: array of {fx, fy, fz} objects
    const netForce = forces.reduce((net, force) => ({
      fx: net.fx + force.fx,
      fy: net.fy + force.fy,
      fz: net.fz + (force.fz || 0)
    }), {fx: 0, fy: 0, fz: 0});
    
    const magnitude = Math.sqrt(netForce.fx**2 + netForce.fy**2 + netForce.fz**2);
    
    return { ...netForce, magnitude };
  }
  
  // Apply Newton's second law
  calculateAcceleration(netForce, mass) {
    return {
      ax: netForce.fx / mass,
      ay: netForce.fy / mass,
      az: (netForce.fz || 0) / mass,
      magnitude: netForce.magnitude / mass
    };
  }
  
  // Analyze inclined plane motion
  analyzeInclinedPlane(mass, angle, mu_static, mu_kinetic, appliedForce = 0) {
    const angleRad = angle * Math.PI / 180;
    const weight = mass * this.g;
    const normalForce = weight * Math.cos(angleRad);
    const gravityComponent = weight * Math.sin(angleRad);
    
    // Check if object will slide
    const maxStaticFriction = mu_static * normalForce;
    const netForceDownPlane = gravityComponent - appliedForce;
    
    if (netForceDownPlane <= maxStaticFriction) {
      return {
        motion: 'static',
        acceleration: 0,
        frictionForce: netForceDownPlane,
        normalForce: normalForce
      };
    } else {
      const kineticFriction = mu_kinetic * normalForce;
      const netForce = gravityComponent - kineticFriction - appliedForce;
      const acceleration = netForce / mass;
      
      return {
        motion: 'sliding',
        acceleration: acceleration,
        frictionForce: kineticFriction,
        normalForce: normalForce
      };
    }
  }
  
  // Simulate Atwood machine
  simulateAtwoodMachine(m1, m2) {
    const totalMass = m1 + m2;
    const netForce = Math.abs(m1 - m2) * this.g;
    const acceleration = netForce / totalMass;
    const tension = (2 * m1 * m2 * this.g) / totalMass;
    
    return {
      acceleration: acceleration,
      tension: tension,
      direction: m1 > m2 ? 'm1 down' : 'm2 down'
    };
  }
  
  // Analyze circular motion forces
  analyzeCircularMotion(mass, radius, velocity) {
    const centripetalAcceleration = velocity**2 / radius;
    const centripetalForce = mass * centripetalAcceleration;
    const period = 2 * Math.PI * radius / velocity;
    const frequency = 1 / period;
    
    return {
      centripetalForce: centripetalForce,
      centripetalAcceleration: centripetalAcceleration,
      period: period,
      frequency: frequency,
      angularVelocity: velocity / radius
    };
  }
}
\`\`\`

## Engineering Applications

### Structural Analysis
- Bridge and building load calculations
- Material stress and strain analysis
- Safety factor determination

### Mechanical Systems
- Machine design and optimization
- Gear and pulley systems
- Vibration analysis

### Aerospace Engineering
- Rocket thrust and trajectory calculations
- Aircraft lift and drag forces
- Orbital mechanics

### Robotics
- Joint torques and forces
- Balance and stability analysis
- Actuator sizing and control`
        },
        {
          type: 'interactive_exercise',
          title: 'Force Analysis and Dynamics Solver',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Build a comprehensive force analysis tool that:

1. Creates and analyzes free body diagrams
2. Solves complex force systems using Newton's laws
3. Simulates motion under various force conditions
4. Handles friction, tension, and normal forces
5. Provides step-by-step solution methods

Your tool should solve:
- Inclined plane problems with friction
- Atwood machine configurations
- Circular motion force analysis
- Multi-body force systems
- Static equilibrium problems

Create a solver that handles complex dynamics problems systematically.`,
            initialCode: `class DynamicsSolver {
  constructor() {
    this.gravity = 9.81;
    this.solutions = [];
  }
  
  // Solve inclined plane with friction
  solveInclinedPlane(mass, angle, mu_s, mu_k, appliedForce = 0, forceAngle = 0) {
    // Analyze forces on inclined plane
    // Consider static vs kinetic friction
    // Return: { motion: string, acceleration: number, forces: object }
  }
  
  // Solve Atwood machine system
  solveAtwoodMachine(m1, m2, pulleyMass = 0) {
    // Calculate acceleration and tension for connected masses
    // Consider pulley mass if provided
    // Return: { acceleration: number, tension: number, forces: object }
  }
  
  // Analyze circular motion forces
  analyzeCircularMotion(mass, radius, speed, bankAngle = 0) {
    // Calculate centripetal force requirements
    // Handle banked curves if angle provided
    // Return: { centripetalForce: number, normalForce: number, friction: number }
  }
  
  // Solve static equilibrium
  solveEquilibrium(forces, moments = []) {
    // Check if system is in static equilibrium
    // forces: array of force vectors
    // moments: array of moment vectors
    // Return: { inEquilibrium: boolean, resultant: object, solution: string }
  }
  
  // Generate step-by-step solution
  generateSolution(problemType, parameters) {
    // Create detailed solution steps
    // Include free body diagrams, equations, calculations
    // Return: { steps: array, finalAnswer: object, explanation: string }
  }
  
  // Simulate motion over time
  simulateMotion(initialConditions, forces, duration, dt = 0.01) {
    // Numerical integration of motion equations
    // initialConditions: { x0, y0, vx0, vy0 }
    // forces: function that returns force at each time step
    // Return: { trajectory: array, velocity: array, acceleration: array }
  }
}

// Force calculation utilities
const ForceUtils = {
  // Calculate friction force
  friction: (normal, mu, isStatic = true, appliedForce = 0) => {
    if (isStatic) {
      return Math.min(mu * normal, appliedForce);
    } else {
      return mu * normal;
    }
  },
  
  // Resolve force components
  resolveForce: (magnitude, angle) => ({
    fx: magnitude * Math.cos(angle * Math.PI / 180),
    fy: magnitude * Math.sin(angle * Math.PI / 180)
  }),
  
  // Vector addition
  addVectors: (vectors) => vectors.reduce((sum, v) => ({
    fx: sum.fx + v.fx,
    fy: sum.fy + v.fy
  }), {fx: 0, fy: 0})
};

// Test implementation
const solver = new DynamicsSolver();

// Test 1: Block on 30° incline, mass 5kg, μs=0.3, μk=0.2
const inclineResult = solver.solveInclinedPlane(5, 30, 0.3, 0.2);
console.log("Inclined plane motion:", inclineResult.motion);
console.log("Acceleration:", inclineResult.acceleration.toFixed(2) + " m/s²");

// Test 2: Atwood machine with 3kg and 2kg masses
const atwoodResult = solver.solveAtwoodMachine(3, 2);
console.log("Atwood acceleration:", atwoodResult.acceleration.toFixed(2) + " m/s²");
console.log("Tension:", atwoodResult.tension.toFixed(2) + " N");

// Test 3: Circular motion - 1kg mass, 2m radius, 5 m/s
const circularResult = solver.analyzeCircularMotion(1, 2, 5);
console.log("Centripetal force:", circularResult.centripetalForce.toFixed(2) + " N");`,
            expectedOutput: `Inclined plane motion: sliding
Acceleration: 2.94 m/s²
Atwood acceleration: 1.96 m/s²
Tension: 23.54 N
Centripetal force: 12.50 N`,
            hints: [
              'For inclined plane: resolve weight into parallel and perpendicular components',
              'Check if mg*sin(θ) > μs*mg*cos(θ) to determine if sliding occurs',
              'Atwood machine: a = |m1-m2|*g/(m1+m2), T = 2*m1*m2*g/(m1+m2)',
              'Circular motion: Fc = mv²/r, must be provided by friction and normal forces',
              'Always draw free body diagrams and apply ΣF = ma in component form',
              'For 30° incline with given friction: expect sliding with a ≈ 2.94 m/s²'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Force Analysis Project',
          title: 'Complex Dynamics Problem Solving',
          points: 100,
          dueWeek: 9
        }
      ]
    },
    {
      id: 'energy-momentum',
      title: 'Energy and Momentum',
      duration: '4 weeks',
      learningObjectives: [
        'Apply conservation of energy principles to solve problems',
        'Understand work-energy theorem and its applications',
        'Analyze collisions using conservation of momentum',
        'Connect energy concepts to computational applications'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Work, Energy, and Conservation Laws',
          content: `# Energy and Momentum

## Work and Energy

### Work Definition
Work is the energy transferred to or from an object via the application of force along a displacement.

**Mathematical Definition**: W = F⃗ · d⃗ = Fd cos θ

Where θ is the angle between force and displacement vectors.

### Types of Energy

#### Kinetic Energy
Energy of motion: KE = ½mv²

#### Potential Energy
**Gravitational**: PE = mgh (near Earth's surface)
**Elastic**: PE = ½kx² (for springs)
**General**: PE = -∫F⃗ · dr⃗

### Work-Energy Theorem
The net work done on an object equals its change in kinetic energy:
Wₙₑₜ = ΔKE = KEf - KEᵢ

### Conservation of Energy
In a closed system with no non-conservative forces:
E = KE + PE = constant

**With non-conservative forces**:
Wₙc = ΔE = ΔKE + ΔPE

## Momentum and Impulse

### Linear Momentum
p⃗ = mv⃗

### Impulse-Momentum Theorem
The impulse delivered to an object equals its change in momentum:
J⃗ = ∫F⃗dt = Δp⃗ = pf - pᵢ

### Conservation of Momentum
In an isolated system (no external forces):
Σpᵢ = Σpf

## Collisions

### Elastic Collisions
- Kinetic energy is conserved
- Momentum is conserved
- Objects bounce apart

**1D Elastic Collision Formulas**:
v₁f = ((m₁ - m₂)v₁ᵢ + 2m₂v₂ᵢ)/(m₁ + m₂)
v₂f = ((m₂ - m₁)v₂ᵢ + 2m₁v₁ᵢ)/(m₁ + m₂)

### Inelastic Collisions
- Momentum is conserved
- Kinetic energy is not conserved
- Some energy converted to heat, sound, deformation

**Perfectly Inelastic**: Objects stick together
vf = (m₁v₁ᵢ + m₂v₂ᵢ)/(m₁ + m₂)

### Coefficient of Restitution
e = (v₂f - v₁f)/(v₁ᵢ - v₂ᵢ)
- e = 1: perfectly elastic
- e = 0: perfectly inelastic
- 0 < e < 1: partially inelastic

## Computational Implementation

\`\`\`javascript
class EnergyMomentumAnalyzer {
  constructor() {
    this.g = 9.81;
  }
  
  // Calculate kinetic energy
  kineticEnergy(mass, velocity) {
    if (typeof velocity === 'number') {
      return 0.5 * mass * velocity * velocity;
    } else {
      // Vector velocity
      const speed = Math.sqrt(velocity.x**2 + velocity.y**2 + (velocity.z || 0)**2);
      return 0.5 * mass * speed * speed;
    }
  }
  
  // Calculate gravitational potential energy
  gravitationalPE(mass, height) {
    return mass * this.g * height;
  }
  
  // Calculate elastic potential energy
  elasticPE(k, displacement) {
    return 0.5 * k * displacement * displacement;
  }
  
  // Analyze energy conservation
  analyzeEnergyConservation(initialState, finalState) {
    const initialEnergy = initialState.kinetic + initialState.potential;
    const finalEnergy = finalState.kinetic + finalState.potential;
    const energyLoss = initialEnergy - finalEnergy;
    
    return {
      initialEnergy,
      finalEnergy,
      energyLoss,
      percentLoss: (energyLoss / initialEnergy) * 100,
      conserved: Math.abs(energyLoss) < 0.01
    };
  }
  
  // Simulate 1D elastic collision
  elasticCollision1D(m1, v1i, m2, v2i) {
    const totalMass = m1 + m2;
    const v1f = ((m1 - m2) * v1i + 2 * m2 * v2i) / totalMass;
    const v2f = ((m2 - m1) * v2i + 2 * m1 * v1i) / totalMass;
    
    // Verify conservation laws
    const initialMomentum = m1 * v1i + m2 * v2i;
    const finalMomentum = m1 * v1f + m2 * v2f;
    const initialKE = 0.5 * m1 * v1i**2 + 0.5 * m2 * v2i**2;
    const finalKE = 0.5 * m1 * v1f**2 + 0.5 * m2 * v2f**2;
    
    return {
      v1f, v2f,
      momentumConserved: Math.abs(initialMomentum - finalMomentum) < 0.001,
      energyConserved: Math.abs(initialKE - finalKE) < 0.001,
      initialKE, finalKE
    };
  }
  
  // Simulate inelastic collision
  inelasticCollision(m1, v1i, m2, v2i, e = 0) {
    const totalMass = m1 + m2;
    
    // Conservation of momentum
    const vf = (m1 * v1i + m2 * v2i) / totalMass;
    
    // For coefficient of restitution e
    const v1f = e * v2i + (1 - e) * vf;
    const v2f = e * v1i + (1 - e) * vf;
    
    const initialKE = 0.5 * m1 * v1i**2 + 0.5 * m2 * v2i**2;
    const finalKE = 0.5 * m1 * v1f**2 + 0.5 * m2 * v2f**2;
    const energyLoss = initialKE - finalKE;
    
    return {
      v1f: e === 0 ? vf : v1f,
      v2f: e === 0 ? vf : v2f,
      energyLoss,
      percentEnergyLoss: (energyLoss / initialKE) * 100
    };
  }
  
  // Analyze pendulum motion
  analyzePendulum(mass, length, initialAngle) {
    const angleRad = initialAngle * Math.PI / 180;
    const maxHeight = length * (1 - Math.cos(angleRad));
    const maxSpeed = Math.sqrt(2 * this.g * maxHeight);
    const period = 2 * Math.PI * Math.sqrt(length / this.g);
    
    return {
      maxHeight,
      maxSpeed,
      period,
      frequency: 1 / period,
      totalEnergy: mass * this.g * maxHeight
    };
  }
}
\`\`\`

## Applications

### Game Development
- Physics engines for realistic collisions
- Energy-based damage systems
- Projectile and explosion effects
- Character movement mechanics

### Engineering
- Crash test analysis
- Energy efficiency calculations
- Vibration and oscillation analysis
- Power system design

### Scientific Computing
- Molecular dynamics simulations
- Particle physics calculations
- Orbital mechanics
- Climate modeling`
        },
        {
          type: 'interactive_exercise',
          title: 'Energy and Collision Simulator',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Create a comprehensive energy and momentum analysis tool:

1. Simulate various types of collisions (elastic, inelastic, explosions)
2. Analyze energy transformations in mechanical systems
3. Model pendulum and oscillatory motion
4. Verify conservation laws numerically
5. Optimize systems for energy efficiency

Your simulator should handle:
- 1D and 2D collision analysis
- Energy conservation in mechanical systems
- Pendulum and spring-mass systems
- Coefficient of restitution effects
- Multi-body collision scenarios

Build a tool that demonstrates conservation laws through interactive simulation.`,
            initialCode: `class PhysicsSimulator {
  constructor() {
    this.gravity = 9.81;
    this.timeStep = 0.01;
  }
  
  // Simulate 2D collision between two objects
  simulate2DCollision(obj1, obj2, e = 1) {
    // obj1/obj2: { mass, vx, vy, x, y, radius }
    // e: coefficient of restitution
    // Return: updated velocities and collision analysis
  }
  
  // Analyze energy transformations
  analyzeEnergySystem(system, timePoints) {
    // Track kinetic, potential, and total energy over time
    // system: { objects: array, forces: array, constraints: array }
    // Return: { time: array, kinetic: array, potential: array, total: array }
  }
  
  // Simulate pendulum motion
  simulatePendulum(length, mass, initialAngle, damping = 0, duration = 10) {
    // Model pendulum with optional damping
    // Return: { time: array, angle: array, energy: array, phase: array }
  }
  
  // Analyze spring-mass system
  analyzeSpringMass(mass, k, x0, v0, damping = 0, duration = 10) {
    // Simple harmonic motion with optional damping
    // Return: { time: array, position: array, velocity: array, energy: array }
  }
  
  // Optimize energy efficiency
  optimizeEfficiency(system, constraints, objective) {
    // Find parameters that maximize/minimize energy objective
    // constraints: { maxForce, maxSpeed, etc. }
    // objective: 'minimize_energy_loss', 'maximize_output', etc.
    // Return: { optimal_params: object, efficiency: number, analysis: object }
  }
  
  // Verify conservation laws
  verifyConservation(simulation_data) {
    // Check if momentum and energy are conserved within tolerance
    // Return: { momentum_conserved: boolean, energy_conserved: boolean, errors: object }
  }
}

// Conservation law verification utilities
const ConservationUtils = {
  // Calculate total momentum of system
  totalMomentum: (objects) => {
    return objects.reduce((total, obj) => ({
      px: total.px + obj.mass * obj.vx,
      py: total.py + obj.mass * obj.vy
    }), {px: 0, py: 0});
  },
  
  // Calculate total kinetic energy
  totalKineticEnergy: (objects) => {
    return objects.reduce((total, obj) => {
      return total + 0.5 * obj.mass * (obj.vx**2 + obj.vy**2);
    }, 0);
  },
  
  // Check conservation within tolerance
  isConserved: (initial, final, tolerance = 0.01) => {
    const percentChange = Math.abs((final - initial) / initial) * 100;
    return percentChange < tolerance;
  }
};

// Test implementation
const simulator = new PhysicsSimulator();

// Test elastic collision: 2kg at 5m/s hits 3kg at rest
const collision = simulator.simulate2DCollision(
  { mass: 2, vx: 5, vy: 0 },
  { mass: 3, vx: 0, vy: 0 },
  1.0 // perfectly elastic
);

console.log("After collision:");
console.log("Object 1 velocity:", collision.obj1.vx.toFixed(2) + " m/s");
console.log("Object 2 velocity:", collision.obj2.vx.toFixed(2) + " m/s");
console.log("Energy conserved:", collision.energyConserved);

// Test pendulum: 1m length, 30° initial angle
const pendulum = simulator.simulatePendulum(1.0, 1.0, 30);
console.log("Pendulum period:", pendulum.period.toFixed(2) + " s");
console.log("Max speed:", pendulum.maxSpeed.toFixed(2) + " m/s");`,
            expectedOutput: `After collision:
Object 1 velocity: -1.00 m/s
Object 2 velocity: 4.00 m/s
Energy conserved: true
Pendulum period: 2.01 s
Max speed: 2.29 m/s`,
            hints: [
              'For elastic collision: use conservation of momentum and energy simultaneously',
              '2kg at 5m/s hitting 3kg at rest: final velocities are -1 m/s and 4 m/s',
              'Pendulum period: T = 2π√(L/g), independent of mass and initial angle (small angles)',
              'Max speed at bottom: v = √(2gh) where h = L(1 - cos θ)',
              'Energy conservation: total energy should remain constant in ideal systems',
              'Use numerical integration for complex motion with damping or nonlinear forces'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Energy Conservation Lab',
          title: 'Conservation Laws in Mechanical Systems',
          points: 100,
          dueWeek: 13
        }
      ]
    },
    {
      id: 'applications-modeling',
      title: 'Physics Applications and Computational Modeling',
      duration: '2 weeks',
      learningObjectives: [
        'Apply physics principles to real-world technology problems',
        'Create computational models of physical systems',
        'Understand physics applications in computer science and engineering',
        'Develop simulation and modeling skills'
      ],
      content: [
        {
          type: 'lesson',
          title: 'Physics in Technology and Simulation',
          content: `# Physics Applications and Modeling

## Physics in Computer Science

### Game Physics Engines
- **Collision Detection**: Spatial partitioning, bounding volumes
- **Rigid Body Dynamics**: Rotation, angular momentum, torque
- **Particle Systems**: Fluids, fire, smoke, explosions
- **Soft Body Physics**: Cloth, deformable objects
- **Constraint Systems**: Joints, springs, ropes

### Computer Graphics
- **Ray Tracing**: Light propagation and reflection
- **Animation**: Realistic motion paths and timing
- **Lighting Models**: Phong, physically-based rendering
- **Fluid Simulation**: Water, air flow visualization
- **Procedural Generation**: Physics-based terrain

### Machine Learning Applications
- **Physics-Informed Neural Networks (PINNs)**
- **Optimization Algorithms**: Gradient descent as physical process
- **Data Analysis**: Statistical mechanics principles
- **Signal Processing**: Wave physics and Fourier analysis

## Computational Physics Methods

### Numerical Integration
- **Euler Method**: Simple first-order integration
- **Runge-Kutta**: Higher-order accuracy
- **Verlet Integration**: Stable for oscillatory systems
- **Symplectic Integrators**: Energy conservation

### Finite Element Analysis
- **Mesh Generation**: Discretizing continuous systems
- **Boundary Conditions**: Applying constraints
- **Solving Linear Systems**: Matrix methods
- **Convergence Analysis**: Ensuring accuracy

### Monte Carlo Methods
- **Random Sampling**: Statistical physics simulations
- **Importance Sampling**: Efficient computation
- **Markov Chain Monte Carlo**: Complex system evolution
- **Error Estimation**: Statistical uncertainty

## Real-World Applications

### Robotics
- **Kinematics**: Robot arm positioning
- **Dynamics**: Joint torques and forces
- **Balance and Stability**: Inverted pendulum control
- **Path Planning**: Optimal trajectory calculation

### Automotive Engineering
- **Collision Analysis**: Crash simulation and safety
- **Suspension Systems**: Spring-damper dynamics
- **Aerodynamics**: Drag and lift calculations
- **Fuel Efficiency**: Energy optimization

### Aerospace
- **Orbital Mechanics**: Satellite trajectory planning
- **Rocket Propulsion**: Thrust and momentum analysis
- **Structural Analysis**: Material stress and fatigue
- **Flight Dynamics**: Control and stability

## Simulation Implementation

\`\`\`javascript
class PhysicsEngine {
  constructor(dt = 0.016) { // 60 FPS
    this.dt = dt;
    this.objects = [];
    this.forces = [];
    this.constraints = [];
  }
  
  // Add object to simulation
  addObject(object) {
    // object: { mass, position, velocity, acceleration, forces }
    this.objects.push({
      mass: object.mass,
      pos: {...object.position},
      vel: {...object.velocity},
      acc: {x: 0, y: 0, z: 0},
      forces: [],
      ...object
    });
  }
  
  // Apply force to object
  applyForce(objectIndex, force) {
    if (this.objects[objectIndex]) {
      this.objects[objectIndex].forces.push(force);
    }
  }
  
  // Update simulation one time step
  update() {
    this.objects.forEach(obj => {
      // Calculate net force
      const netForce = obj.forces.reduce((net, force) => ({
        x: net.x + force.x,
        y: net.y + force.y,
        z: net.z + (force.z || 0)
      }), {x: 0, y: 0, z: 0});
      
      // Apply Newton's second law
      obj.acc.x = netForce.x / obj.mass;
      obj.acc.y = netForce.y / obj.mass;
      obj.acc.z = (netForce.z || 0) / obj.mass;
      
      // Verlet integration for stability
      this.verletIntegration(obj);
      
      // Clear forces for next iteration
      obj.forces = [];
    });
    
    // Apply constraints
    this.applyConstraints();
  }
  
  verletIntegration(obj) {
    // Store previous position
    const oldPos = {...obj.pos};
    
    // Update position: x(t+dt) = 2x(t) - x(t-dt) + a*dt²
    if (!obj.prevPos) {
      // First step - use Euler method
      obj.prevPos = {...oldPos};
      obj.pos.x += obj.vel.x * this.dt + 0.5 * obj.acc.x * this.dt * this.dt;
      obj.pos.y += obj.vel.y * this.dt + 0.5 * obj.acc.y * this.dt * this.dt;
      obj.pos.z += (obj.vel.z || 0) * this.dt + 0.5 * obj.acc.z * this.dt * this.dt;
    } else {
      // Verlet step
      const newX = 2 * obj.pos.x - obj.prevPos.x + obj.acc.x * this.dt * this.dt;
      const newY = 2 * obj.pos.y - obj.prevPos.y + obj.acc.y * this.dt * this.dt;
      const newZ = 2 * obj.pos.z - obj.prevPos.z + obj.acc.z * this.dt * this.dt;
      
      obj.prevPos = oldPos;
      obj.pos.x = newX;
      obj.pos.y = newY;
      obj.pos.z = newZ;
    }
    
    // Update velocity: v = (x(t) - x(t-dt)) / dt
    obj.vel.x = (obj.pos.x - obj.prevPos.x) / this.dt;
    obj.vel.y = (obj.pos.y - obj.prevPos.y) / this.dt;
    obj.vel.z = (obj.pos.z - obj.prevPos.z) / this.dt;
  }
  
  applyConstraints() {
    this.constraints.forEach(constraint => {
      constraint.apply(this.objects);
    });
  }
  
  // Collision detection between spheres
  detectCollisions() {
    const collisions = [];
    for (let i = 0; i < this.objects.length; i++) {
      for (let j = i + 1; j < this.objects.length; j++) {
        const obj1 = this.objects[i];
        const obj2 = this.objects[j];
        
        if (obj1.radius && obj2.radius) {
          const dx = obj1.pos.x - obj2.pos.x;
          const dy = obj1.pos.y - obj2.pos.y;
          const distance = Math.sqrt(dx*dx + dy*dy);
          const minDistance = obj1.radius + obj2.radius;
          
          if (distance < minDistance) {
            collisions.push({obj1, obj2, distance, normal: {x: dx/distance, y: dy/distance}});
          }
        }
      }
    }
    return collisions;
  }
  
  // Resolve elastic collisions
  resolveCollisions(collisions) {
    collisions.forEach(collision => {
      const {obj1, obj2, normal} = collision;
      
      // Relative velocity
      const relVel = {
        x: obj1.vel.x - obj2.vel.x,
        y: obj1.vel.y - obj2.vel.y
      };
      
      // Relative velocity in collision normal direction
      const velAlongNormal = relVel.x * normal.x + relVel.y * normal.y;
      
      // Do not resolve if velocities are separating
      if (velAlongNormal > 0) return;
      
      // Restitution coefficient (1 = perfectly elastic)
      const e = 1.0;
      
      // Calculate impulse magnitude
      const j = -(1 + e) * velAlongNormal / (1/obj1.mass + 1/obj2.mass);
      
      // Apply impulse
      const impulse = {x: j * normal.x, y: j * normal.y};
      
      obj1.vel.x += impulse.x / obj1.mass;
      obj1.vel.y += impulse.y / obj1.mass;
      obj2.vel.x -= impulse.x / obj2.mass;
      obj2.vel.y -= impulse.y / obj2.mass;
    });
  }
  
  // Get system energy
  getTotalEnergy() {
    let kinetic = 0;
    let potential = 0;
    
    this.objects.forEach(obj => {
      // Kinetic energy
      const speed = Math.sqrt(obj.vel.x**2 + obj.vel.y**2 + (obj.vel.z||0)**2);
      kinetic += 0.5 * obj.mass * speed * speed;
      
      // Gravitational potential energy (assuming y is vertical)
      potential += obj.mass * 9.81 * obj.pos.y;
    });
    
    return {kinetic, potential, total: kinetic + potential};
  }
}

// Example: Bouncing ball simulation
class BouncingBallDemo {
  constructor() {
    this.engine = new PhysicsEngine(0.016);
    this.setupScene();
  }
  
  setupScene() {
    // Add bouncing ball
    this.engine.addObject({
      mass: 1,
      position: {x: 0, y: 10, z: 0},
      velocity: {x: 5, y: 0, z: 0},
      radius: 0.5,
      restitution: 0.8
    });
    
    // Add gravity force
    setInterval(() => {
      this.engine.objects.forEach((obj, index) => {
        this.engine.applyForce(index, {x: 0, y: -obj.mass * 9.81, z: 0});
      });
    }, 16);
  }
  
  simulate(steps = 1000) {
    const trajectory = [];
    
    for (let i = 0; i < steps; i++) {
      // Ground collision
      this.engine.objects.forEach(obj => {
        if (obj.pos.y - obj.radius <= 0 && obj.vel.y < 0) {
          obj.pos.y = obj.radius;
          obj.vel.y *= -obj.restitution;
        }
      });
      
      this.engine.update();
      
      // Record trajectory
      if (i % 10 === 0) {
        trajectory.push({
          time: i * this.engine.dt,
          position: {...this.engine.objects[0].pos},
          velocity: {...this.engine.objects[0].vel},
          energy: this.engine.getTotalEnergy()
        });
      }
    }
    
    return trajectory;
  }
}
\`\`\`

## Industry Applications

### Virtual Reality
- **Head tracking**: Accurate position and orientation
- **Hand tracking**: Natural gesture recognition
- **Physics interaction**: Realistic object manipulation
- **Motion sickness**: Understanding vestibular system

### Autonomous Vehicles
- **Sensor fusion**: Combining multiple data sources
- **Trajectory planning**: Safe and efficient paths
- **Collision avoidance**: Predictive safety systems
- **Control systems**: Stable vehicle dynamics

### Medical Devices
- **Biomechanics**: Joint and muscle analysis
- **Surgical robots**: Precise tool control
- **Prosthetics**: Natural movement patterns
- **Diagnostic imaging**: Physics of medical scans`
        },
        {
          type: 'interactive_exercise',
          title: 'Physics Engine Development',
          component: 'InteractiveExercise',
          exerciseData: {
            instructions: `Build a complete physics engine for real-world applications:

1. Implement rigid body dynamics with rotation
2. Create advanced collision detection and response
3. Add constraint systems (springs, joints, ropes)
4. Develop particle systems for effects
5. Optimize performance for real-time simulation

Your engine should support:
- Multiple integration methods (Euler, Verlet, RK4)
- Collision detection algorithms (broad and narrow phase)
- Constraint solving and stabilization
- Energy tracking and conservation
- Performance profiling and optimization

Create a physics engine suitable for games or scientific simulation.`,
            initialCode: `class AdvancedPhysicsEngine {
  constructor(options = {}) {
    this.dt = options.timeStep || 1/60;
    this.integrator = options.integrator || 'verlet';
    this.objects = [];
    this.constraints = [];
    this.broadPhase = new SpatialHash(options.cellSize || 1);
    this.performance = { updateTime: 0, collisionTime: 0, constraintTime: 0 };
  }
  
  // Add rigid body with rotation support
  addRigidBody(properties) {
    // properties: { mass, position, velocity, angle, angularVelocity, shape }
    // Support: position, rotation, linear/angular motion
  }
  
  // Advanced collision detection system
  detectCollisions() {
    // Implement broad phase (spatial hashing) and narrow phase (SAT, GJK)
    // Handle: sphere-sphere, box-box, sphere-box, polygon-polygon
    // Return: detailed collision information
  }
  
  // Constraint system (springs, joints, motors)
  addConstraint(type, objectA, objectB, parameters) {
    // type: 'spring', 'distance', 'hinge', 'motor'
    // Implement constraint forces and stabilization
  }
  
  // Particle system for effects
  createParticleSystem(emitter, properties) {
    // emitter: { position, rate, lifetime }
    // properties: { velocity, acceleration, size, color }
    // Return: particle system object
  }
  
  // Advanced integration methods
  integrate(obj, method = this.integrator) {
    // Support: 'euler', 'verlet', 'rk4', 'leapfrog'
    // Handle both linear and angular motion
  }
  
  // Performance optimization
  optimizePerformance() {
    // Spatial partitioning, sleeping objects, LOD
    // Return: optimization statistics
  }
  
  // Complete simulation step
  step() {
    const startTime = performance.now();
    
    // Update physics
    this.updateForces();
    this.solveConstraints();
    this.detectAndResolveCollisions();
    this.integrateMotion();
    
    // Track performance
    this.performance.updateTime = performance.now() - startTime;
    
    return this.getSystemState();
  }
}

// Spatial hashing for broad phase collision detection
class SpatialHash {
  constructor(cellSize = 1) {
    this.cellSize = cellSize;
    this.grid = new Map();
  }
  
  // Hash object to grid cells
  hashObject(obj) {
    // Calculate which cells object occupies
    // Add to appropriate grid cells
  }
  
  // Get potential collision pairs
  getPotentialPairs() {
    // Return pairs of objects in same grid cells
  }
}

// Collision detection algorithms
const CollisionAlgorithms = {
  // Separating Axes Theorem for polygons
  SAT: (objA, objB) => {
    // Test separation along all potential axes
    // Return: collision info or null
  },
  
  // Gilbert-Johnson-Keerthi for complex shapes
  GJK: (objA, objB) => {
    // Support function-based collision detection
    // Return: collision detection result
  }
};

// Test implementation - bouncing balls in container
const engine = new AdvancedPhysicsEngine({ timeStep: 1/60 });

// Add container walls
engine.addRigidBody({
  type: 'static',
  shape: 'box',
  position: { x: 0, y: -5 },
  size: { width: 20, height: 1 }
});

// Add bouncing balls
for (let i = 0; i < 10; i++) {
  engine.addRigidBody({
    type: 'dynamic',
    shape: 'circle',
    mass: 1,
    position: { x: Math.random() * 10 - 5, y: Math.random() * 5 + 2 },
    velocity: { x: Math.random() * 4 - 2, y: 0 },
    radius: 0.3,
    restitution: 0.8
  });
}

// Simulate for 5 seconds
const duration = 5.0;
const steps = Math.floor(duration / engine.dt);
const results = [];

for (let i = 0; i < steps; i++) {
  const state = engine.step();
  if (i % 60 === 0) { // Record every second
    results.push({
      time: i * engine.dt,
      objects: state.objects.length,
      energy: state.totalEnergy,
      performance: { ...engine.performance }
    });
  }
}

console.log("Simulation completed:");
console.log("Average update time:", results.reduce((sum, r) => sum + r.performance.updateTime, 0) / results.length);
console.log("Energy conservation:", Math.abs(results[0].energy - results[results.length-1].energy) / results[0].energy * 100 + "%");`,
            expectedOutput: `Simulation completed:
Average update time: 2.34ms
Energy conservation: 5.2%`,
            hints: [
              'Use spatial hashing to reduce collision detection from O(n²) to O(n)',
              'Implement Verlet integration for better energy conservation',
              'SAT algorithm: test separation along all edge normals of both shapes',
              'Add constraint stabilization to prevent drift in joints',
              'Profile performance and optimize bottlenecks (usually collision detection)',
              'Energy loss of 5-10% is typical for numerical integration with collisions'
            ]
          }
        }
      ],
      assessments: [
        {
          type: 'Physics Engine Project',
          title: 'Complete Physics Simulation System',
          points: 150,
          dueWeek: 15
        }
      ]
    }
  ],
  assessments: [
    {
      id: 'midterm',
      title: 'Midterm Examination',
      type: 'exam',
      date: 'Week 8',
      duration: '90 minutes',
      points: 150,
      description: 'Comprehensive exam covering motion, forces, and dynamics with computational problem-solving components.'
    },
    {
      id: 'final',
      title: 'Final Examination',
      type: 'exam',
      date: 'Week 15',
      duration: '120 minutes',
      points: 200,
      description: 'Cumulative exam covering all physics topics with emphasis on energy, momentum, and real-world applications.'
    },
    {
      id: 'computational-project',
      title: 'Physics Simulation and Modeling Project',
      type: 'project',
      date: 'Week 15',
      duration: '2 weeks',
      points: 175,
      description: 'Independent project developing a physics simulation for a real-world application with analysis and optimization.'
    }
  ],
  resources: [
    {
      category: 'Textbooks',
      items: [
        'Physics for Scientists and Engineers by Serway & Jewett',
        'University Physics by Young & Freedman',
        'Introduction to Classical Mechanics by David Morin',
        'Computational Physics by Nicholas Giordano'
      ]
    },
    {
      category: 'Software Tools',
      items: [
        'Python with NumPy and Matplotlib for calculations',
        'MATLAB/Octave for numerical analysis',
        'PhET Physics Simulations for visualization',
        'Tracker video analysis software',
        'Jupyter Notebooks for interactive computing'
      ]
    },
    {
      category: 'Online Resources',
      items: [
        'Khan Academy Physics Course',
        'MIT 8.01 Classical Mechanics lectures',
        'Physics Classroom online tutorials',
        'Wolfram Alpha for calculations and visualization',
        'GitHub physics simulation repositories'
      ]
    }
  ],
  gradingScale: {
    'A': '93-100%',
    'A-': '90-92%',
    'B+': '87-89%',
    'B': '83-86%',
    'B-': '80-82%',
    'C+': '77-79%',
    'C': '73-76%',
    'C-': '70-72%',
    'D': '60-69%',
    'F': 'Below 60%'
  },
  gradingBreakdown: {
    'Module Assessments': '25%',
    'Laboratory Work': '20%',
    'Midterm Exam': '20%',
    'Final Exam': '25%',
    'Computational Project': '10%'
  },
  attendancePolicy: 'Regular attendance essential for laboratory work and computational exercises. Active participation required in problem-solving sessions.',
  latePolicy: 'Physics assignments penalized 10% per day late. Laboratory reports must be submitted within one week of completion.',
  accommodations: 'Accommodations available including extended time for problem-solving, alternative formats for visual materials, and assistive technology for calculations.',
  academicIntegrity: 'Original problem solutions required with clear work shown. Collaboration on concepts encouraged with individual accountability for solutions.',
  technologyRequirements: 'Computer with mathematical software, scientific calculator, and internet access for simulations and computational work.',
  softwareRequirements: [
    'Mathematical software (Python, MATLAB, or equivalent)',
    'Graphing and visualization tools',
    'Scientific calculator or computer algebra system',
    'Video analysis software for laboratory work',
    'Simulation environments for physics modeling'
  ],
  courseEvaluation: 'Student feedback collected through concept surveys, laboratory effectiveness assessments, and computational project reviews.'
};